import { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface NotificationBellProps {
  onClick?: () => void;
}

// Sonido de notificación usando Web Audio API
const playNotificationSound = () => {
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;

    const ctx = new AudioContextClass();
    const now = ctx.currentTime;

    // Primer tono (más agudo)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(880, now);
    osc1.frequency.exponentialRampToValueAtTime(440, now + 0.15);
    gain1.gain.setValueAtTime(0.15, now);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.2);

    // Segundo tono (más grave, eco)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(660, now + 0.1);
    osc2.frequency.exponentialRampToValueAtTime(330, now + 0.3);
    gain2.gain.setValueAtTime(0.1, now + 0.1);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now + 0.1);
    osc2.stop(now + 0.35);
  } catch {
    // Silenciosamente ignorar si el audio no está disponible
  }
};

export default function NotificationBell({ onClick }: NotificationBellProps) {
  const [pendingCount, setPendingCount] = useState(0);
  const [lastViewedCount, setLastViewedCount] = useState(() => {
    try {
      return parseInt(localStorage.getItem('bell_last_viewed') || '0', 10);
    } catch {
      return 0;
    }
  });
  const [isShaking, setIsShaking] = useState(false);
  const prevCountRef = useRef(0);

  const unreadCount = pendingCount > lastViewedCount ? pendingCount - lastViewedCount : 0;

  const fetchPendingCount = useCallback(async () => {
    const { count, error } = await supabase
      .from('sales')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'pending');

    if (!error && count !== null) {
      const currentLastViewed = parseInt(localStorage.getItem('bell_last_viewed') || '0', 10);
      const currentUnread = count > currentLastViewed ? count - currentLastViewed : 0;
      // Si hay nuevos pedidos no vistos, suena y anima
      if (currentUnread > 0 && currentUnread !== (prevCountRef.current > currentLastViewed ? prevCountRef.current - currentLastViewed : 0)) {
        playNotificationSound();
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 1500);
      }
      prevCountRef.current = count;
      setPendingCount(count);
      setLastViewedCount(currentLastViewed);
    }
  }, []);

  useEffect(() => {
    fetchPendingCount();

    // Actualizar cada 30 segundos
    const interval = setInterval(fetchPendingCount, 30000);

    // También actualizar cuando la ventana vuelve a estar visible
    const handleVisibility = () => {
      if (!document.hidden) fetchPendingCount();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchPendingCount]);

  const handleClick = () => {
    // Marcar como visto: guardar el conteo actual
    localStorage.setItem('bell_last_viewed', String(pendingCount));
    setLastViewedCount(pendingCount);
    onClick?.();
  };

  return (
    <button
      onClick={handleClick}
      className="relative p-2 rounded-full hover:bg-gray-100 transition-colors cursor-pointer flex items-center justify-center"
      title="Pedidos pendientes"
      style={isShaking ? {
        animation: 'bellShake 0.6s ease-in-out',
      } : undefined}
    >
      <style>{`
        @keyframes bellShake {
          0% { transform: rotate(0deg); }
          15% { transform: rotate(15deg); }
          30% { transform: rotate(-15deg); }
          45% { transform: rotate(10deg); }
          60% { transform: rotate(-10deg); }
          75% { transform: rotate(5deg); }
          100% { transform: rotate(0deg); }
        }
        @keyframes badgePulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.3); }
          100% { transform: scale(1); }
        }
        .badge-pulse {
          animation: badgePulse 0.5s ease-in-out 2;
        }
      `}</style>
      <div className="w-6 h-6 flex items-center justify-center">
        <i className="ri-notification-3-line text-gray-700 text-xl"></i>
      </div>
      {unreadCount > 0 && (
        <span
          className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 ${isShaking ? 'badge-pulse' : ''}`}
        >
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </button>
  );
}